import { v4 } from 'uuid'
import { cloneDeep } from 'lodash'

import FirestoreService from '../services/firestoreService'

import helpers from '../commonHelpers/helpers'
import { getConvertedData } from '../models/interface.helper'
import {
  getRiderTeamMembersFromDb,
  getTeamMemberObject,
  mergeRidersTeamMembersWithPaperworkTabData,
} from '../helpers/teamMembers'
import useToasterHelper from '../helpers/ToasterHelper'
import { sortByRiderRole } from '../helpers/registration-on-event'

import { useAppDispatch, useAppSelector } from '../store/hooks'

import {
  selectPaperworkTabData,
  selectRegistration,
  selectRegistrationByDay,
  selectRidersTeamMembersR,
  setPaperworkTabData,
  setRegistrationAc,
  setRidersTeamMembers,
} from '../store/registration/registrationSlice'

import { RiderTeamMemberModel } from '../models/rider-team-member/riderTeamMember.model'

import { IRiderTeamMemberInterface } from '../models/rider-team-member/riderTeamMember.interface'
import { IPaperworkTab_ } from '../models/event-registered-users/event-registered-users.interface'
import { ITeamMember } from '../models/users/user.interface'
import { IRegistrationByDayInterface } from '../models/registrations-by-day/registrationByDay.interface'

import { CONST } from '../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

const updateRidersTeamMember = async (ridersTeamMember: IRiderTeamMemberInterface) => {
  await FirestoreService.updateItem(
    COLLECTIONS.RIDER_TEAM_MEMBER.NAME,
    ridersTeamMember.id,
    new RiderTeamMemberModel(ridersTeamMember).toFirestore()
  )
}

const deleteRiderTeamMember = async (idOfDocToRemove: string) => {
  await FirestoreService.deleteItem(COLLECTIONS.RIDER_TEAM_MEMBER.NAME, idOfDocToRemove)
}

const createRiderTeamMember = async (ridersTeamMember: IRiderTeamMemberInterface) => {
  return (
    (await FirestoreService.createItem(COLLECTIONS.RIDER_TEAM_MEMBER.NAME, ridersTeamMember))?.id ??
    null
  )
}

const useTeamMembers = () => {
  const dispatch = useAppDispatch()
  const toastFunctions = useToasterHelper()

  const registration = useAppSelector(selectRegistration)
  const paperworkTabData = useAppSelector(selectPaperworkTabData)
  const registrationsByDay = useAppSelector(selectRegistrationByDay)
  const ridersTeamMembers = useAppSelector(selectRidersTeamMembersR)

  const saveCartRidersTeamMembers = async (
    ridersTeamMembersUpdated: IRiderTeamMemberInterface[],
    paperworkTabDataUpdated: IPaperworkTab_[]
  ) => {
    if (!registration) return

    const registrationUpdated = {
      ...registration,
      cartItems: {
        ...registration?.cartItems,
        ridersTeamMembers: ridersTeamMembersUpdated,
      },
    }

    await FirestoreService.updateItem(
      COLLECTIONS.EVENT_REGISTERED_USERS.NAME,
      registration?.id,
      registrationUpdated
    )

    dispatch(setRidersTeamMembers(ridersTeamMembersUpdated))
    dispatch(setPaperworkTabData(paperworkTabDataUpdated))
    dispatch(setRegistrationAc(registrationUpdated))
  }

  const getPaperworkTabData = (
    registrationsByDay: IRegistrationByDayInterface[],
    ridersTeamMembers: IRiderTeamMemberInterface[]
  ) => {
    let ridersTeamMembersUpdated: IRiderTeamMemberInterface[] = []
    const paperworkTabDataUnique: IPaperworkTab_[] = []

    registrationsByDay
      .filter((item) => !item.isDivision)
      .forEach((registrationByDay) => {
        registrationByDay.horsesSelected.forEach((horse) => {
          const isExistPaperwork = paperworkTabDataUnique.find(
            (current) =>
              current.horseId === horse.id && current.riderId === registrationByDay.riderId
          )

          if (!isExistPaperwork) {
            paperworkTabDataUnique.push(
              getConvertedData({
                riderDob: registrationByDay.riderDob || null,
                riderName: registrationByDay.riderName || null,
                riderId: registrationByDay.riderId || null,
                riderEmail: registrationByDay.riderEmail || null,
                riderProfilePicture: registrationByDay.riderProfilePicture || null,
                riderCountry: registrationByDay.riderCountry || null,
                riderNameNGram: registrationByDay.riderNameNGram || [],

                registrationDocId: registrationByDay.registrationDocId,

                horseId: horse.id || null,
                horseName: horse.name || null,
                horseProfilePicture: horse.picture || null,

                ridersTeamMembers: [],
              })
            )
          }
        })
      })

    paperworkTabDataUnique.forEach((paperwork) => {
      ridersTeamMembers.forEach((item) => {
        if (
          item.riderId === paperwork.riderId &&
          item.horseId === paperwork.horseId &&
          item.teamMemberRole !== CONST.UI.TEAM_MEMBERS_ROLES.RIDER
        ) {
          ridersTeamMembersUpdated.push({ ...item })
        }
      })

      ridersTeamMembersUpdated.push(
        getTeamMemberObject({
          eventRegisteredUser: registration,
          paperwork: paperwork,
          teamMember: {
            teamMemberId: paperwork.riderId || null,
            teamMemberDob: paperwork.riderDob || null,
            teamMemberName: paperwork.riderName,
            teamMemberRole: CONST.UI.TEAM_MEMBERS_ROLES.RIDER || null,
            teamMemberEmail: paperwork.riderEmail || null,
            teamMemberProfilePicture: paperwork.riderProfilePicture || null,
          },
        })
      )
    })

    paperworkTabDataUnique.forEach((paperwork) => {
      // // Add "Trainer" role if not exist
      const haveTrainer = ridersTeamMembersUpdated.some(
        (item) =>
          item.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.TRAINER &&
          item.riderId === paperwork.riderId &&
          item.horseId === paperwork.horseId
      )

      if (!haveTrainer)
        ridersTeamMembersUpdated.push(
          getTeamMemberObject({
            eventRegisteredUser: registration,
            paperwork: paperwork,
            teamMember: {
              teamMemberId: null,
              teamMemberDob: null,
              teamMemberName: null,
              teamMemberRole: CONST.UI.TEAM_MEMBERS_ROLES.TRAINER || null,
              teamMemberEmail: null,
              teamMemberProfilePicture: null,
            },
          })
        )

      // Add Owner as default role
      const haveOwner = ridersTeamMembersUpdated.some(
        (item) =>
          item.teamMemberRole === CONST.UI.TEAM_MEMBERS_ROLES.OWNER &&
          item.riderId === paperwork.riderId &&
          item.horseId === paperwork.horseId
      )

      if (!haveOwner) {
        ridersTeamMembersUpdated.push(
          getTeamMemberObject({
            eventRegisteredUser: registration,
            paperwork: paperwork,
            teamMember: {
              teamMemberId: null,
              teamMemberDob: null,
              teamMemberName: null,
              teamMemberRole: CONST.UI.TEAM_MEMBERS_ROLES.OWNER || null,
              teamMemberEmail: null,
              teamMemberProfilePicture: null,
            },
          })
        )
      }
    })

    ridersTeamMembersUpdated = sortByRiderRole(ridersTeamMembersUpdated)

    return {
      paperworkTabDataUnique: paperworkTabDataUnique,
      ridersTeamMembersUpdated: ridersTeamMembersUpdated,
    }
  }

  const generateRidersTeamMembers = async () => {
    const { paperworkTabDataUnique, ridersTeamMembersUpdated } = getPaperworkTabData(
      registrationsByDay,
      ridersTeamMembers
    )

    const paperworkTabDataUpdated = mergeRidersTeamMembersWithPaperworkTabData({
      paperworkTabData: paperworkTabDataUnique,
      ridersTeamMembers: ridersTeamMembersUpdated,
    })

    dispatch(setRidersTeamMembers(ridersTeamMembersUpdated))
    dispatch(setPaperworkTabData(paperworkTabDataUpdated))

    return { ridersTeamMembersUpdated }
  }

  const saveRidersTeamMembers = async ({
    ridersTeamMembersToAdd,
    ridersTeamMembersToDelete,
    ridersTeamMembersToUpdate,
  }: {
    ridersTeamMembersToAdd?: IRiderTeamMemberInterface[]
    ridersTeamMembersToDelete?: IRiderTeamMemberInterface[]
    ridersTeamMembersToUpdate?: IRiderTeamMemberInterface[]
  }) => {
    let id: string | null = null

    if (ridersTeamMembersToAdd && ridersTeamMembersToAdd.length > 0) {
      let paperworkTabDataNotFilled = ridersTeamMembersToAdd.filter(
        (ridersTeamMember) => !ridersTeamMember.teamMemberRole || !ridersTeamMember.teamMemberId
      )

      let { emptyVarName, emptyVarValue } = helpers.findEmptyVal({
        paperworkTabDataNotFilled: paperworkTabDataNotFilled && !paperworkTabDataNotFilled.length,
      })

      if (emptyVarName) {
        console.error(`${emptyVarName} is [${emptyVarValue}]`)
        return null
      }
    }

    try {
      // Add
      await helpers.asyncWhileLoop({
        loopCount: ridersTeamMembersToAdd?.length ?? 0,
        functionToFire: async (currIndex) => {
          id = await createRiderTeamMember(
            new RiderTeamMemberModel(ridersTeamMembersToAdd![currIndex]).toFirestore()
          )
          ridersTeamMembersToAdd![currIndex] = getConvertedData({
            ...ridersTeamMembersToAdd![currIndex],
            id: id ?? null,
          })
        },
      })

      // Delete
      await helpers.asyncWhileLoop({
        loopCount: ridersTeamMembersToDelete?.length ?? 0,
        functionToFire: async (currIndex) => {
          if (!ridersTeamMembersToDelete![currIndex].id) return
          await deleteRiderTeamMember(ridersTeamMembersToDelete![currIndex].id!)
        },
      })

      // Update
      await helpers.asyncWhileLoop({
        loopCount: ridersTeamMembersToUpdate?.length ?? 0,
        functionToFire: async (currIndex) => {
          await updateRidersTeamMember({
            ...new RiderTeamMemberModel(ridersTeamMembersToUpdate![currIndex]).toFirestore(),
            id: ridersTeamMembersToUpdate![currIndex].id,
          })
        },
      })

      const ridersTeamMembersInDb__ =
        (await getRiderTeamMembersFromDb(registration?.id ?? '')) || []

      const paperworkTabData__ = mergeRidersTeamMembersWithPaperworkTabData({
        paperworkTabData,
        ridersTeamMembers: ridersTeamMembersInDb__,
      })

      dispatch(setRidersTeamMembers(ridersTeamMembersInDb__))
      dispatch(setPaperworkTabData(paperworkTabData__))

      return { ridersTeamMembersToAdd, ridersTeamMembersToUpdate }
    } catch (error) {
      console.error(error, 'error')
    }
  }

  const onAddRiderButtonClick = async ({
    paperworkIndex,
    row,
  }: {
    paperworkIndex: number
    row: IPaperworkTab_
  }) => {
    const paperworkTabDataUpdated = cloneDeep(paperworkTabData)
    const ridersTeamMembersUpdated: IRiderTeamMemberInterface[] = []

    paperworkTabDataUpdated[paperworkIndex].ridersTeamMembers.push(
      getConvertedData({
        ...new RiderTeamMemberModel({
          id: `${v4()}-${Date.now()}`,

          riderDob: row?.riderDob || null,
          riderName: row?.riderName || null,
          riderEmail: row?.riderEmail || null,
          riderId: row?.riderId || null,
          riderNameNGram: row?.riderNameNGram || [],
          riderProfilePicture: row?.riderProfilePicture || null,
          riderCountry: row?.riderCountry || null,

          mailLog: [],

          teamMemberId: null,
          teamMemberName: null,
          teamMemberProfilePicture: null,
          teamMemberRole: null,
          teamMemberDob: null,
          teamMemberEmail: null,

          userId: registration?.userId || null,

          eventId: registration?.eventId || null,
          eventName: registration?.eventName || null,

          registrationDocId: registration?.id || null,

          horseId: row.horseId,
          horseName: row.horseName,
          horseProfilePicture: row.horseProfilePicture || null,

          modified: new Date().toString(),
          created: new Date().toString(),
        }).toObject(),
      })
    )

    paperworkTabDataUpdated.forEach((paperwork) => {
      if (paperwork.ridersTeamMembers.length > 0) {
        ridersTeamMembersUpdated.push(...paperwork.ridersTeamMembers)
      }
    })

    await saveCartRidersTeamMembers(ridersTeamMembersUpdated, paperworkTabDataUpdated)
  }

  const onRemoveRiderTeamMemberClick = async ({
    paperworkIndex,
    teamMemberIndex,
  }: {
    paperworkIndex: number
    teamMemberIndex: number
  }) => {
    const paperworkTabDataUpdated = cloneDeep(paperworkTabData)
    const ridersTeamMembersUpdated: IRiderTeamMemberInterface[] = []

    try {
      paperworkTabDataUpdated[paperworkIndex].ridersTeamMembers.splice(teamMemberIndex, 1)

      paperworkTabDataUpdated.forEach((paperwork) => {
        if (paperwork.ridersTeamMembers.length > 0) {
          ridersTeamMembersUpdated.push(...paperwork.ridersTeamMembers)
        }
      })

      await saveCartRidersTeamMembers(ridersTeamMembersUpdated, paperworkTabDataUpdated)
    } catch (error) {
      console.error(error, 'error')
    }
  }

  const onRiderTeamMemberRoleChange = async ({
    teamMemberId,
    teamMemberRole,
    paperworkIndex,
    ridersTeamMemberIndex,
  }: {
    paperworkIndex: number
    teamMemberId: IRiderTeamMemberInterface['teamMemberId']
    teamMemberRole: IRiderTeamMemberInterface['teamMemberRole']
    ridersTeamMemberIndex: number
  }) => {
    try {
      const paperworkTabDataUpdated = cloneDeep(paperworkTabData)
      const ridersTeamMembersUpdated: IRiderTeamMemberInterface[] = []

      if (!teamMemberId) {
        toastFunctions.info({
          message: `Please select team member first`,
        })
        return
      }

      paperworkTabDataUpdated[paperworkIndex].ridersTeamMembers[
        ridersTeamMemberIndex
      ].teamMemberRole = teamMemberRole

      paperworkTabDataUpdated.forEach((paperwork) => {
        if (paperwork.ridersTeamMembers.length > 0) {
          ridersTeamMembersUpdated.push(...paperwork.ridersTeamMembers)
        }
      })

      await saveCartRidersTeamMembers(ridersTeamMembersUpdated, paperworkTabDataUpdated)
    } catch (error) {
      console.error(error, 'error')
    }
  }

  const onRiderTeamMemberChange = async ({
    paperworkIndex,
    currentTeamMember,
    ridersTeamMemberIndex,
  }: {
    paperworkIndex: number
    currentTeamMember: ITeamMember
    ridersTeamMemberIndex: number
  }) => {
    const paperworkTabDataUpdated = cloneDeep(paperworkTabData)
    const ridersTeamMembersUpdated: IRiderTeamMemberInterface[] = []

    const ridersTeamMember =
      paperworkTabDataUpdated[paperworkIndex].ridersTeamMembers[ridersTeamMemberIndex]

    paperworkTabDataUpdated[paperworkIndex].ridersTeamMembers[ridersTeamMemberIndex] = {
      ...ridersTeamMember,
      teamMemberEmail: currentTeamMember.memberEmail || null,
      teamMemberId: currentTeamMember.memberId,
      teamMemberName: currentTeamMember.memberName,
      teamMemberProfilePicture: currentTeamMember.memberProfilePicture,
      teamMemberDob: currentTeamMember.memberDob,
    }

    paperworkTabDataUpdated.forEach((paperwork) => {
      if (paperwork.ridersTeamMembers.length > 0) {
        ridersTeamMembersUpdated.push(...paperwork.ridersTeamMembers)
      }
    })

    await saveCartRidersTeamMembers(ridersTeamMembersUpdated, paperworkTabDataUpdated)
  }

  return {
    generateRidersTeamMembers,
    saveRidersTeamMembers,
    onAddRiderButtonClick,
    onRemoveRiderTeamMemberClick,
    onRiderTeamMemberRoleChange,
    onRiderTeamMemberChange,
    saveCartRidersTeamMembers,
    getPaperworkTabData,
  }
}
export default useTeamMembers
